import { z } from 'zod';

/**
 * Shared building blocks of the HTTP contracts (docs/API_DESIGN.md §1). Every object is strict:
 * a handler that returns an undeclared field fails, so data cannot leak by accident.
 */
export const businessDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
export const instantSchema = z.iso.datetime();
export const uuidSchema = z.uuid();

/** A rate in [0, 1], or `null` when its denominator is 0 (never 0 %). */
export const rateSchema = z.number().min(0).max(1).nullable();

export const MILESTONES = [
  'NEW',
  'CONTACTED',
  'QUALIFIED',
  'VISIT_SCHEDULED',
  'VISIT_COMPLETED',
  'WON',
] as const;
export const milestoneSchema = z.enum(MILESTONES);

export const UNITS = [
  'leads',
  'deals',
  'containers',
  'containers_per_deal',
  'visits',
  'tasks',
  'ratio',
  'minutes',
  'hours',
  'seconds',
  'calls',
  'money',
] as const;
export type Unit = (typeof UNITS)[number];

/** A figure with its value in the comparison period (docs/API_DESIGN.md §1.2). */
export const metricSchema = z.strictObject({
  value: z.number().nullable(),
  previous: z.number().nullable(),
  /** `value − previous`; for ratios, in ratio points (0.031 = +3.1 pp). */
  change: z.number().nullable(),
  /** `change / previous`; `null` for ratios and when `previous` is 0. */
  changeRatio: z.number().nullable(),
  unit: z.enum(UNITS),
});
export type MetricDto = z.output<typeof metricSchema>;

/** A metric that may be impossible to compute with the connected CRM (stage-based visits). */
export const availableMetricSchema = metricSchema.extend({ available: z.boolean() });

export const dataFreshnessSchema = z.strictObject({
  lastSyncedAt: instantSchema.nullable(),
  status: z.enum(['fresh', 'delayed', 'stale']),
  /** `demo`: demo data is generated on demand and is fresh by construction. */
  evaluatedAs: z.enum(['sync', 'demo']),
});

const baseMeta = {
  asOf: instantSchema,
  dataFreshness: dataFreshnessSchema,
  datasetKind: z.enum(['demo', 'live']).nullable(),
  definitionVersions: z.record(z.string(), z.number().int()),
  /** Set when the response was restricted to one salesperson (row-level scope). */
  scope: z.strictObject({ salespersonId: uuidSchema.nullable() }),
};

export const periodMetaSchema = z.strictObject({
  from: businessDateSchema,
  to: businessDateSchema,
  timezone: z.string(),
  utcStart: instantSchema,
  utcEnd: instantSchema,
});

/** Meta of period-based analytics endpoints. */
export const metaSchema = z.strictObject({
  period: periodMetaSchema,
  comparisonPeriod: z.strictObject({ from: businessDateSchema, to: businessDateSchema }).nullable(),
  ...baseMeta,
});
export type MetaDto = z.output<typeof metaSchema>;

/** Meta of state endpoints that have no period (attention, system status, filters). */
export const snapshotMetaSchema = z.strictObject(baseMeta);
export type SnapshotMetaDto = z.output<typeof snapshotMetaSchema>;

export const envelope = <T extends z.ZodType>(data: T) =>
  z.strictObject({ data, meta: metaSchema });
export const snapshotEnvelope = <T extends z.ZodType>(data: T) =>
  z.strictObject({ data, meta: snapshotMetaSchema });

export const sourceRefSchema = z.strictObject({
  /** `null` = "Unknown": leads whose source could not be derived. */
  id: uuidSchema.nullable(),
  code: z.string().nullable(),
  name: z.string().nullable(),
});

/** RFC 9457 problem details (docs/API_DESIGN.md §1.3). */
export const problemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  requestId: z.string().optional(),
  errors: z.array(z.strictObject({ path: z.string(), message: z.string() })).optional(),
});
export type ProblemDto = z.output<typeof problemSchema>;
